import { Link } from 'react-router-dom';
import { DataTable } from '@features/admin-v2/components/data-table';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material';

import { buildAdminIntakeHubPath } from '../adminJourneyRouting';
import type { IntakeVerificationPractice, IntakeVerificationQueueResponse } from '../types';

import { IntakeStatusChip } from './IntakeStatusChip';

export const VERIFICATION_FILTERS = [
  { value: 'awaiting', label: 'Awaiting verification' },
  { value: 'all', label: 'All' },
  { value: 'verified', label: 'Verified' },
  { value: 'incomplete', label: 'Incomplete' },
] as const;

export type VerificationFilter = (typeof VERIFICATION_FILTERS)[number]['value'];

function dateLabel(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Date unavailable' : date.toLocaleDateString();
}

function ReviewChip({ state }: { state: IntakeVerificationPractice['verification']['state'] }) {
  return (
    <IntakeStatusChip
      status={state === 'verified' ? 'accepted' : state === 'awaiting' ? 'in_review' : 'missing'}
      label={
        state === 'verified'
          ? 'Verified'
          : state === 'awaiting'
            ? 'Awaiting verification'
            : 'Incomplete'
      }
    />
  );
}

export function IntakeVerificationQueue({
  data,
  returnTo,
  loading,
  failed,
  search,
  filter,
  page,
  perPage,
  onSearch,
  onFilter,
  onPage,
  onPerPage,
  onRefresh,
}: {
  data?: IntakeVerificationQueueResponse;
  returnTo?: string;
  loading: boolean;
  failed: boolean;
  search: string;
  filter: VerificationFilter;
  page: number;
  perPage: number;
  onSearch: (value: string) => void;
  onFilter: (value: VerificationFilter) => void;
  onPage: (value: number) => void;
  onPerPage: (value: number) => void;
  onRefresh: () => void;
}): React.JSX.Element {
  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          label="Search practice name"
          slotProps={{ htmlInput: { maxLength: 200 } }}
          value={search}
          onChange={event => onSearch(event.target.value)}
          size="small"
          fullWidth
        />
        <TextField
          select
          label="Review status"
          value={filter}
          onChange={event => {
            const option = VERIFICATION_FILTERS.find(item => item.value === event.target.value);
            if (option) onFilter(option.value);
          }}
          size="small"
          sx={{ minWidth: 220 }}
        >
          {VERIFICATION_FILTERS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" onClick={onRefresh} disabled={loading}>
          Refresh
        </Button>
      </Stack>
      {failed ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" onClick={onRefresh}>
              Retry
            </Button>
          }
        >
          Could not load the verification queue. Refresh to try again.
        </Alert>
      ) : (
        <>
          <Box aria-live="polite">
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              {VERIFICATION_FILTERS.map(option => (
                <Box
                  key={option.value}
                  sx={{
                    flex: 1,
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {option.value === 'all' ? 'All practices' : option.label}
                  </Typography>
                  <Typography variant="h5">
                    {data
                      ? data.practice_counts[option.value === 'all' ? 'total' : option.value]
                      : '—'}
                  </Typography>
                </Box>
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Eligible practices matching your search. Counts include every result page.
            </Typography>
          </Box>
          <DataTable<IntakeVerificationPractice>
            title="Verification queue"
            data={data?.items ?? []}
            total={data?.total ?? 0}
            loading={loading}
            emptyMessage="No practices match these filters."
            columns={[
              {
                key: 'organization_name',
                label: 'Practice',
                render: (_value, row) => (
                  <Stack spacing={1}>
                    <Typography sx={{ fontWeight: 600 }}>{row.organization_name}</Typography>
                    <ReviewChip state={row.verification.state} />
                    <Typography variant="caption">
                      {row.verification.provider_counts.verified} verified ·{' '}
                      {row.verification.provider_counts.awaiting} awaiting ·{' '}
                      {row.verification.provider_counts.incomplete} incomplete
                    </Typography>
                    {row.providers.length < row.verification.provider_counts.total && (
                      <Typography variant="caption" color="text.secondary">
                        Legacy provider intakes are excluded from review.
                      </Typography>
                    )}
                  </Stack>
                ),
              },
              {
                key: 'providers',
                label: 'Providers',
                render: (_value, row) => (
                  <Stack spacing={2}>
                    {row.providers.map(provider => (
                      <Stack key={provider.user_id} spacing={0.5}>
                        <Typography variant="body2">
                          {provider.display_name || 'Provider name unavailable'}
                        </Typography>
                        <Box>
                          <ReviewChip state={provider.state} />
                        </Box>
                        <Button
                          component={Link}
                          size="small"
                          to={buildAdminIntakeHubPath({
                            organizationId: row.organization_id,
                            stage: 'verification',
                            providerScope: provider.user_id,
                            returnTo,
                          })}
                        >
                          Review provider
                        </Button>
                        <Typography variant="caption" color="text.secondary">
                          {provider.journeys_pending
                            ? 'Enrollment pending'
                            : provider.verified_at
                              ? `Checked ${dateLabel(provider.verified_at)}`
                              : provider.submitted_at
                                ? `Submitted ${dateLabel(provider.submitted_at)}`
                                : 'Not submitted'}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                ),
              },
              {
                key: 'verification',
                label: 'Oldest outstanding submission',
                render: (_value, row) => dateLabel(row.verification.submitted_at),
              },
              {
                key: 'organization_id',
                label: 'Review',
                render: (_value, row) => (
                  <Button
                    component={Link}
                    to={buildAdminIntakeHubPath({
                      organizationId: row.organization_id,
                      stage: 'verification',
                      returnTo,
                    })}
                    variant="outlined"
                    size="small"
                    aria-label={`Review intake for ${row.organization_name}`}
                  >
                    Review intake
                  </Button>
                ),
              },
            ]}
          />
          <TablePagination
            component="div"
            count={data?.total ?? 0}
            page={page - 1}
            rowsPerPage={perPage}
            rowsPerPageOptions={[10, 20, 50]}
            onPageChange={(_event, next) => onPage(next + 1)}
            onRowsPerPageChange={event => onPerPage(Number(event.target.value))}
            labelRowsPerPage="Practices per page"
          />
        </>
      )}
    </Stack>
  );
}
